'use client'

import { useState, useEffect, useRef, Suspense } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import Navbar from '../../components/Navbar'
import { useAuth } from '../../contexts/AuthContext'
import AddressAutocomplete from '../../components/AddressAutocomplete'
import { geocodeAddress } from '@/lib/geocode'
import { calculateDistance } from '@/lib/distance'
import PaymentButton from '../../components/PaymentButton'
import { authenticatedFetch, authenticatedGet, authenticatedPost } from '@/lib/client-fetch'
import { formatCalendarDate, formatTimeString, parseCalendarDate, getCentralTodayString } from '@/lib/time-utils'

interface Hub {
  hub: string
  routes: Array<{
    routeId: string
    destination: string
    driverId: string
    driverName: string
    driverVehicle: string | null
    driverPhone: string
    departureTime: string
    schedules: Array<{
      day: string
      pickupTime: string
    }>
    departureDay?: string | null // For routes without schedules
    isRecurring?: boolean
    expiresAt?: string | null // Legacy field, kept for backward compatibility
    routeDate?: string | null // The actual date when a one-time route runs
    extraInfo?: string | null
  }>
  distance?: number
}

interface Student {
  id: string
  name: string
  email: string
  phone: string
  campus: string | null
}

interface SelectedRun {
  hub: string
  date: string
  timeSlot: {
    routeId: string
    driverId: string
    driverName: string
    driverVehicle: string | null
    driverPhone: string
    timeWindow: string
  }
}

function NewOrderPageContent() {
  const { user, isLoading } = useAuth()
  const router = useRouter()

  // Step management
  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5 | 6>(1)
  
  // Selection mode: 'hub' or 'address'
  const [selectionMode, setSelectionMode] = useState<'hub' | 'address'>('hub')
  const [homeAddress, setHomeAddress] = useState('')
  const [homeAddressCoords, setHomeAddressCoords] = useState<{ lat: number; lng: number } | null>(null)
  const [searchRadius, setSearchRadius] = useState<number>(20) // miles
  
  // Data
  const [hubs, setHubs] = useState<Hub[]>([])
  const [filteredHubs, setFilteredHubs] = useState<Hub[]>([]) // Filtered hubs when using address
  const [students, setStudents] = useState<Student[]>([])
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null)
  const [selectedHub, setSelectedHub] = useState<string | null>(null)
  const [selectedDropoff, setSelectedDropoff] = useState<string | null>(null)
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [selectedRun, setSelectedRun] = useState<SelectedRun | null>(null)
  const [availableDates, setAvailableDates] = useState<Date[]>([])
  const [timeSlots, setTimeSlots] = useState<Array<{
    routeId: string
    driverId: string
    driverName: string
    driverVehicle: string | null
    driverPhone: string
    timeWindow: string
    extraInfo?: string | null
  }>>([])
  
  // Loading states
  const [loadingHubs, setLoadingHubs] = useState(true)
  const [loadingStudents, setLoadingStudents] = useState(true)
  const [geocodingAddress, setGeocodingAddress] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [orderId, setOrderId] = useState<string | null>(null)
  const [orderAmount, setOrderAmount] = useState<number>(0)
  const [confirmationData, setConfirmationData] = useState<any>(null)
  const [paymentError, setPaymentError] = useState('')
  const [error, setError] = useState('')
  const [credits, setCredits] = useState<number>(0)
  const [useCredits, setUseCredits] = useState<boolean>(false)
  const [payingWithCredits, setPayingWithCredits] = useState<boolean>(false)
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false)
  
  // Promo code
  const [promoCode, setPromoCode] = useState('')
  const [promoCodeData, setPromoCodeData] = useState<{
    id: string
    code: string
    fixedPrice: number
    description: string | null
  } | null>(null)
  const [validatingPromoCode, setValidatingPromoCode] = useState(false)
  const [promoCodeError, setPromoCodeError] = useState('')
  
  // Bag image upload
  const [bagImageFile, setBagImageFile] = useState<File | null>(null)
  const [bagImagePreview, setBagImagePreview] = useState<string | null>(null)
  const [bagImageError, setBagImageError] = useState<string | null>(null)

  // Fetch hubs on mount (don't filter by date - we'll filter client-side)
  useEffect(() => {
    const fetchHubs = async () => {
      try {
        setLoadingHubs(true)
        const timezoneOffsetMinutes = -new Date().getTimezoneOffset()
        const response = await authenticatedGet(`/api/routes/available-runs?timezoneOffset=${timezoneOffsetMinutes}`)
        if (!response.ok) throw new Error('Failed to fetch hubs')
        const data = await response.json()
        setHubs(data.hubs || [])
        setFilteredHubs(data.hubs || [])
      } catch (error) {
        console.error('Error fetching hubs:', error)
      } finally {
        setLoadingHubs(false)
      }
    }
    fetchHubs()
  }, [])

  // Short-lived capacity memo keyed by (routeId, date) — the filter effect
  // below otherwise refires the full hubs x routes fetch fan-out every time
  // an input changes. 30s matches the endpoint's s-maxage.
  const capacityCacheRef = useRef<Map<string, { atCapacity: boolean; fetchedAt: number }>>(new Map())

  // Filter hubs by proximity when home address is provided
  // Always filter routes by capacity (even on step 1, before date is selected)
  useEffect(() => {
    const filterHubs = async () => {
      let filtered = hubs

      // Filter by proximity if using address mode
      if (selectionMode === 'address' && homeAddressCoords && hubs.length > 0) {
        // For now, show all hubs when using address mode
        // In production, you'd geocode hub origins and filter by distance
        // This requires storing originLat/originLng for each hub or geocoding them
        filtered = hubs.map(hub => ({ ...hub, distance: undefined }))
      }

      // Always filter routes by capacity using routeId (even on step 1, before date is selected)
      // This checks orders with the same routeId and compares against driver capacity
      if (filtered.length > 0) {
        const filteredWithCapacity = await Promise.all(
          filtered.map(async (hub) => {
            const routesWithCapacity = await Promise.all(
              hub.routes.map(async (route) => {
                try {
                  const cacheKey = `${route.routeId}|${selectedDate || ''}`
                  const cached = capacityCacheRef.current.get(cacheKey)
                  if (cached && Date.now() - cached.fetchedAt < 30000) {
                    return cached.atCapacity ? null : route
                  }

                  // Use the new routeId-based capacity endpoint
                  // If date is selected, include it in the query; otherwise check overall capacity
                  const capacityUrl = selectedDate
                    ? `/api/routes/${encodeURIComponent(route.routeId)}/capacity?date=${encodeURIComponent(selectedDate)}`
                    : `/api/routes/${encodeURIComponent(route.routeId)}/capacity`

                  const capacityResponse = await authenticatedGet(capacityUrl)

                  if (capacityResponse.ok) {
                    const capacityData = await capacityResponse.json()

                    // If route is at capacity (currentBags >= capacity), filter it out
                    // Also check that there's at least 1 bag of capacity remaining
                    const atCapacity = !!(capacityData.isAtCapacity || !capacityData.hasCapacity || capacityData.currentBags >= capacityData.capacity || capacityData.remainingCapacity < 1)
                    capacityCacheRef.current.set(cacheKey, { atCapacity, fetchedAt: Date.now() })
                    if (atCapacity) {
                      console.log(`[Hub Filter] Route ${route.routeId} at capacity (${capacityData.currentBags}/${capacityData.capacity} bags), filtering out${selectedDate ? ` for date ${selectedDate}` : ' (overall)'}`)
                      return null
                    }
                  } else {
                    // Fail open - if capacity check fails, still show the route
                    console.warn(`[Hub Filter] Failed to check capacity for route ${route.routeId}, showing route anyway`)
                  }
                } catch (error) {
                  // Fail open - if capacity check fails, still show the route
                  console.error(`[Hub Filter] Error checking capacity for route ${route.routeId}:`, error)
                }
                
                return route
              })
            )
            
            return {
              ...hub,
              routes: routesWithCapacity.filter(route => route !== null) as typeof hub.routes
            }
          })
        )
        
        // Filter out hubs that have no routes left after capacity filtering
        filtered = filteredWithCapacity.filter(hub => hub.routes.length > 0)
      }

      setFilteredHubs(filtered)
    }

    filterHubs()
    // selectedDropoff was in this dep list but is never read in the effect —
    // it only caused a full capacity re-fetch fan-out on every dropoff pick.
  }, [selectionMode, homeAddressCoords, hubs, selectedDate])

  // Handle home address geocoding
  const handleAddressSelect = async (address: string) => {
    setHomeAddress(address)
    setGeocodingAddress(true)
    try {
      const coords = await geocodeAddress(address)
      if (coords) {
        setHomeAddressCoords(coords)
        setSelectionMode('address')
      } else {
        setError('Failed to geocode address. Please try again.')
      }
    } catch (error) {
      console.error('Error geocoding address:', error)
      setError('Failed to geocode address. Please try again.')
    } finally {
      setGeocodingAddress(false)
    }
  }

  // Fetch students linked to parent
  useEffect(() => {
    const fetchStudents = async () => {
      if (isLoading) return // Wait for auth to finish loading
      
      if (!user || user.type !== 'parent') {
        setLoadingStudents(false)
        return
      }
      
      try {
        setLoadingStudents(true)
        const response = await authenticatedGet(`/api/users?parentId=${user.id}`)
        if (!response.ok) throw new Error('Failed to fetch students')
        const data = await response.json()
        const studentList = Array.isArray(data) ? data : (data.students || [])
        const filtered = studentList.filter((s: any) => s.type === 'student')
        setStudents(filtered)
        if (filtered.length === 1) {
          setSelectedStudent(filtered[0])
        }
      } catch (error) {
        console.error('Error fetching students:', error)
      } finally {
        setLoadingStudents(false)
      }
    }
    fetchStudents()
  }, [user, isLoading])

  // Fetch credits when entering payment step
  useEffect(() => {
    if (step === 5 && user?.id) {
      fetchCredits()
    }
  }, [step, user?.id])

  // Calculate available dates when hub is selected
  useEffect(() => {
    if (!selectedHub || hubs.length === 0) {
      setAvailableDates([])
      return
    }

    const hub = hubs.find(h => h.hub === selectedHub)
    if (!hub) {
      console.warn('[Date Calculation] Hub not found:', selectedHub)
      setAvailableDates([])
      return
    }

    console.log('[Date Calculation] Hub found:', hub.hub, 'Routes:', hub.routes.length)
    
    // Get all unique days from schedules OR from route's departureDay
    const days = new Set<string>()
    const oneTimeRouteDates: Date[] = [] // Store specific dates for one-time routes
    
    hub.routes.forEach((route, idx) => {
      console.log(`[Date Calculation] Route ${idx}:`, {
        routeId: route.routeId,
        schedules: route.schedules,
        schedulesCount: route.schedules?.length || 0,
        departureTime: route.departureTime,
        departureDay: route.departureDay,
        isRecurring: route.isRecurring,
        expiresAt: route.expiresAt, // Legacy field
        routeDate: route.routeDate
      })
      
      // For one-time routes, use routeDate or legacy expiresAt (preserving local timezone day)
      if (!route.isRecurring) {
        let routeDate: Date | null = null
        
        if (route.routeDate) {
          // Use routeDate if available - parse ISO string to extract date components
          // route.routeDate is string | null, so if truthy it's a string
          const routeDateStr = route.routeDate
          // Parse YYYY-MM-DD from ISO string (YYYY-MM-DDTHH:mm:ss.sssZ)
          const dateMatch = routeDateStr.match(/^(\d{4})-(\d{2})-(\d{2})/)
          if (dateMatch) {
            const year = parseInt(dateMatch[1], 10)
            const month = parseInt(dateMatch[2], 10) - 1 // month is 0-indexed
            const day = parseInt(dateMatch[3], 10)
            routeDate = new Date(year, month, day, 0, 0, 0, 0)
          } else {
            routeDate = new Date(route.routeDate)
          }
        } else if (route.expiresAt) {
          // Legacy: Parse ISO string to extract date components (avoid timezone conversion)
          // route.expiresAt is string | null, so if truthy it's a string
          const expiresAtStr = route.expiresAt
          // Parse YYYY-MM-DD from ISO string (YYYY-MM-DDTHH:mm:ss.sssZ)
          const dateMatch = expiresAtStr.match(/^(\d{4})-(\d{2})-(\d{2})/)
          if (dateMatch) {
            const year = parseInt(dateMatch[1], 10)
            const month = parseInt(dateMatch[2], 10) - 1 // month is 0-indexed
            const day = parseInt(dateMatch[3], 10)
            // Create new date in local timezone with same day (preserves the day regardless of timezone)
            routeDate = new Date(year, month, day, 0, 0, 0, 0)
          } else {
            // Fallback to old method if format doesn't match
            const expiresAtDate = new Date(route.expiresAt)
            const year = expiresAtDate.getFullYear()
            const month = expiresAtDate.getMonth()
            const day = expiresAtDate.getDate()
            routeDate = new Date(year, month, day, 0, 0, 0, 0)
          }
        }
        
        if (routeDate) {
          // "Today" pinned to Central so available dates match across timezones
          const today = parseCalendarDate(getCentralTodayString()) ?? new Date()

          // Only add if the route date is today or in the future
          if (routeDate >= today) {
            oneTimeRouteDates.push(routeDate)
            console.log('[Date Calculation] Added one-time route date:', routeDate.toISOString(), 'from', route.routeDate ? 'routeDate' : 'expiresAt (legacy)')
          }
        }
        // Skip processing schedules/departureDay for one-time routes - we use the route date
      } else {
        // Use schedules if available (for recurring routes)
        if (route.schedules && route.schedules.length > 0) {
          route.schedules.forEach(schedule => {
            if (schedule.day) {
              days.add(schedule.day)
              console.log('[Date Calculation] Added day from schedule:', schedule.day)
            }
          })
        } else if (route.departureDay) {
          // Fallback: Use departureDay if no schedules exist
          days.add(route.departureDay)
          console.log('[Date Calculation] Added day from departureDay:', route.departureDay)
        }
      }
    })

    // If we have one-time route dates but no recurring days, use those dates
    if (days.size === 0 && oneTimeRouteDates.length > 0) {
      console.log('[Date Calculation] Using one-time route dates:', oneTimeRouteDates.length)
      const uniqueDates = Array.from(new Set(oneTimeRouteDates.map(d => d.getTime())))
        .map(time => new Date(time))
        .sort((a, b) => a.getTime() - b.getTime())
      setAvailableDates(uniqueDates)
      return
    }

    if (days.size === 0) {
      console.warn('[Date Calculation] No schedules found for hub:', selectedHub, 'Routes:', hub.routes.length)
      hub.routes.forEach((route, idx) => {
        console.warn(`[Date Calculation] Route ${idx} has ${route.schedules?.length || 0} schedules`)
      })
      setAvailableDates([])
      return
    }

    console.log('[Date Calculation] Found days:', Array.from(days))

    // Generate dates for next 60 days, starting from today (pinned to Central)
    const dates: Date[] = []
    const today = parseCalendarDate(getCentralTodayString()) ?? new Date()
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    
    // Create a map of day name to day index
    const dayIndexMap: { [key: string]: number } = {
      'Sunday': 0,
      'Monday': 1,
      'Tuesday': 2,
      'Wednesday': 3,
      'Thursday': 4,
      'Friday': 5,
      'Saturday': 6
    }

    // For each scheduled day, find the next occurrence(s)
    days.forEach(dayName => {
      const targetDayIndex = dayIndexMap[dayName]
      if (targetDayIndex === undefined) {
        console.warn('[Date Calculation] Unknown day name:', dayName)
        return
      }

      const currentDayIndex = today.getDay()
      let daysUntilNext = targetDayIndex - currentDayIndex
      
      // If the day has already passed this week (or is today and we want future dates), get next week's occurrence
      if (daysUntilNext <= 0) {
        daysUntilNext += 7
      }

      // Get routes that match this day
      const routesForDay = hub.routes.filter(route => {
        const hasSchedule = route.schedules && route.schedules.length > 0 && route.schedules.some(s => s.day === dayName)
        const hasDepartureDay = (!route.schedules || route.schedules.length === 0) && route.departureDay === dayName
        return hasSchedule || hasDepartureDay
      })

      // Generate occurrences for the next 60 days (about 9 weeks)
      for (let weekOffset = 0; weekOffset < 9; weekOffset++) {
        const date = new Date(today)
        date.setDate(today.getDate() + daysUntilNext + (weekOffset * 7))
        date.setHours(0, 0, 0, 0) // Normalize to start of day
        
        // Verify the date is actually the correct day of week
        if (date.getDay() !== targetDayIndex) {
          console.warn(`[Date Calculation] Date mismatch: Expected ${dayName} (${targetDayIndex}), got ${date.getDay()}`)
          continue
        }
        
        // Check if at least one route for this day is valid for this date
        const isValidDate = routesForDay.some(route => {
          // Recurring routes are always valid
          if (route.isRecurring) return true
          
          // One-time routes: check if date matches routeDate (or legacy expiresAt)
          let routeDate: Date | null = null
          if (route.routeDate) {
            // Parse ISO string to extract date components (avoid timezone conversion)
            const routeDateStr = route.routeDate // routeDate is string | null, so if truthy it's a string
            const dateMatch = routeDateStr.match(/^(\d{4})-(\d{2})-(\d{2})/)
            if (dateMatch) {
              const year = parseInt(dateMatch[1], 10)
              const month = parseInt(dateMatch[2], 10) - 1
              const day = parseInt(dateMatch[3], 10)
              routeDate = new Date(year, month, day, 0, 0, 0, 0)
            } else {
              routeDate = new Date(route.routeDate)
            }
          } else if (route.expiresAt) {
            // Legacy: Parse ISO string to extract date components (avoid timezone conversion)
            const expiresAtStr = route.expiresAt // expiresAt is string | null, so if truthy it's a string
            const dateMatch = expiresAtStr.match(/^(\d{4})-(\d{2})-(\d{2})/)
            if (dateMatch) {
              const year = parseInt(dateMatch[1], 10)
              const month = parseInt(dateMatch[2], 10) - 1
              const day = parseInt(dateMatch[3], 10)
              routeDate = new Date(year, month, day, 0, 0, 0, 0)
            } else {
              const expiresAtDate = new Date(route.expiresAt)
              const year = expiresAtDate.getFullYear()
              const month = expiresAtDate.getMonth()
              const day = expiresAtDate.getDate()
              routeDate = new Date(year, month, day, 0, 0, 0, 0)
            }
          }
          
          if (!routeDate) return true // No route date = valid
          routeDate.setHours(23, 59, 59) // End of route day
          const isValid = date <= routeDate
          console.log(`[Date Calculation] One-time route check: date ${date.toISOString()} <= routeDate ${routeDate.toISOString()}? ${isValid}`)
          return isValid
        })
        
        if (!isValidDate) {
          console.log(`[Date Calculation] Skipping date ${date.toISOString()} - no valid routes`)
          continue // Skip dates after expiration
        }
        
        // Only add dates within the next 60 days and not in the past
        const daysDiff = Math.floor((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
        if (daysDiff > 0 && daysDiff < 60) {
          dates.push(date)
          console.log(`[Date Calculation] Added date: ${date.toISOString()} (${dayName})`)
        }
      }
    })

    // Combine recurring dates with one-time route dates
    const allDates = [...dates, ...oneTimeRouteDates]
    
    // Remove duplicates and sort
    const uniqueDates = Array.from(new Set(allDates.map(d => d.getTime())))
      .map(time => new Date(time))
      .sort((a, b) => a.getTime() - b.getTime())

    console.log('[Date Calculation] Generated dates:', uniqueDates.length, 'First few:', uniqueDates.slice(0, 5).map(d => d.toISOString().slice(0, 10)))
    setAvailableDates(uniqueDates)
  }, [selectedHub, hubs])

  // Fetch time slots when date is selected
  useEffect(() => {
    if (!selectedHub || !selectedDropoff || !selectedDate || hubs.length === 0) {
      setTimeSlots([])
      return
    }

    const hub = hubs.find(h => h.hub === selectedHub)
    if (!hub) {
      console.warn('[Time Slot] Hub not found:', selectedHub)
      setTimeSlots([])
      return
    }

    // Filter routes by selected dropoff location
    const filteredRoutes = hub.routes.filter(r => r.destination === selectedDropoff)
    if (filteredRoutes.length === 0) {
      console.warn('[Time Slot] No routes found for dropoff:', selectedDropoff)
      setTimeSlots([])
      return
    }

    // Parse selectedDate as local date (not UTC) to avoid timezone issues
    // selectedDate is in format "YYYY-MM-DD"
    const [year, month, day] = selectedDate.split('-').map(Number)
    const targetDate = new Date(year, month - 1, day) // month is 0-indexed
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    const dayOfWeek = dayNames[targetDate.getDay()]

    console.log('[Time Slot] Calculating slots for:', {
      hub: selectedHub,
      selectedDate,
      parsedDate: targetDate.toISOString(),
      dayOfWeek,
      dayIndex: targetDate.getDay(),
      routesCount: hub.routes.length
    })

    const calculateSlots = async () => {
      const slots: typeof timeSlots = []
      
      // Check capacity for each route before adding it to slots
      for (const route of filteredRoutes) {
        console.log(`[Time Slot] Checking route:`, {
          routeId: route.routeId,
          schedules: route.schedules,
          schedulesLength: route.schedules?.length || 0,
          departureDay: route.departureDay,
          departureTime: route.departureTime,
          isRecurring: route.isRecurring,
          expiresAt: route.expiresAt, // Legacy field
          routeDate: route.routeDate
        })

        // Check if route has schedules for this day OR departureDay matches
        let hasScheduleForDay = false
        if (route.schedules && route.schedules.length > 0) {
          hasScheduleForDay = route.schedules.some(s => {
            const matches = s.day === dayOfWeek
            console.log(`[Time Slot] Schedule check: ${s.day} === ${dayOfWeek}? ${matches}`)
            return matches
          })
        }
        
        // Check departureDay match (only if no schedules exist)
        const hasDepartureDayMatch = (!route.schedules || route.schedules.length === 0) && route.departureDay === dayOfWeek
        
        // For one-time routes, check if selected date matches routeDate (or legacy expiresAt)
        let isOneTimeRouteMatch = false
        if (!route.isRecurring) {
          let routeDate: Date | null = null
          if (route.routeDate) {
            // Parse ISO string to extract date components (avoid timezone conversion)
            const routeDateStr = route.routeDate // routeDate is string | null, so if truthy it's a string
            const dateMatch = routeDateStr.match(/^(\d{4})-(\d{2})-(\d{2})/)
            if (dateMatch) {
              const year = parseInt(dateMatch[1], 10)
              const month = parseInt(dateMatch[2], 10) - 1
              const day = parseInt(dateMatch[3], 10)
              routeDate = new Date(year, month, day, 0, 0, 0, 0)
            } else {
              routeDate = new Date(route.routeDate)
            }
          } else if (route.expiresAt) {
            // Legacy: Parse ISO string to extract date components (avoid timezone conversion)
            const expiresAtStr = route.expiresAt // expiresAt is string | null, so if truthy it's a string
            const dateMatch = expiresAtStr.match(/^(\d{4})-(\d{2})-(\d{2})/)
            if (dateMatch) {
              const year = parseInt(dateMatch[1], 10)
              const month = parseInt(dateMatch[2], 10) - 1
              const day = parseInt(dateMatch[3], 10)
              routeDate = new Date(year, month, day, 0, 0, 0, 0)
            } else {
              const expiresAtDate = new Date(route.expiresAt)
              const year = expiresAtDate.getFullYear()
              const month = expiresAtDate.getMonth()
              const day = expiresAtDate.getDate()
              routeDate = new Date(year, month, day, 0, 0, 0, 0)
            }
          }
          
          if (routeDate) {
            routeDate.setHours(0, 0, 0, 0) // Start of route day
            const selectedDateObj = new Date(selectedDate)
            selectedDateObj.setHours(0, 0, 0, 0)
            
            // Check if selected date matches route date (or is before it)
            if (selectedDateObj <= routeDate) {
              isOneTimeRouteMatch = true
              console.log(`[Time Slot] One-time route matches selected date (routeDate: ${routeDate.toISOString()}, selected: ${selectedDateObj.toISOString()})`)
            }
          }
        }
        
        console.log(`[Time Slot] Route match check:`, {
          hasScheduleForDay,
          hasDepartureDayMatch,
          isOneTimeRouteMatch,
          dayOfWeek,
          routeDepartureDay: route.departureDay,
          scheduleDays: route.schedules?.map(s => s.day) || []
        })
        
        // Only include routes that match this day OR are one-time routes matching the selected date
        if (!hasScheduleForDay && !hasDepartureDayMatch && !isOneTimeRouteMatch) {
          console.log(`[Time Slot] Route does not match day ${dayOfWeek} and is not a matching one-time route`)
          continue
        }
        
        // Check capacity for this route/date combination using routeId
        try {
          const capacityResponse = await authenticatedGet(
            `/api/routes/${encodeURIComponent(route.routeId)}/capacity?date=${encodeURIComponent(selectedDate)}`
          )
          
          if (capacityResponse.ok) {
            const capacityData = await capacityResponse.json()
            console.log(`[Time Slot] Capacity check for route ${route.routeId}:`, capacityData)
            
            // If route is at capacity (currentBags >= capacity), skip it
            // Also check that there's at least 1 bag of capacity remaining
            if (capacityData.isAtCapacity || !capacityData.hasCapacity || capacityData.currentBags >= capacityData.capacity || capacityData.remainingCapacity < 1) {
              console.log(`[Time Slot] Route ${route.routeId} is at capacity (${capacityData.currentBags}/${capacityData.capacity} bags), skipping`)
              continue
            }
          } else {
            console.warn(`[Time Slot] Failed to check capacity for route ${route.routeId}, showing route anyway (fail open)`)
            // Fail open - if capacity check fails, still show the route
          }
        } catch (error) {
          console.error(`[Time Slot] Error checking capacity for route ${route.routeId}:`, error)
          // Fail open - if capacity check fails, still show the route
        }
        
        // Use departureTime from route (for both recurring and one-time routes)
        const departureTime = route.departureTime
        
        if (!departureTime) {
          console.warn('[Time Slot] Route missing departureTime:', route.routeId)
          continue
        }

        // For one-time routes, check if the selected date is before routeDate (additional validation)
        if (!route.isRecurring) {
          let routeDate: Date | null = null
          if (route.routeDate) {
            // Parse ISO string to extract date components (avoid timezone conversion)
            const routeDateStr = route.routeDate // routeDate is string | null, so if truthy it's a string
            const dateMatch = routeDateStr.match(/^(\d{4})-(\d{2})-(\d{2})/)
            if (dateMatch) {
              const year = parseInt(dateMatch[1], 10)
              const month = parseInt(dateMatch[2], 10) - 1
              const day = parseInt(dateMatch[3], 10)
              routeDate = new Date(year, month, day, 0, 0, 0, 0)
            } else {
              routeDate = new Date(route.routeDate)
            }
          } else if (route.expiresAt) {
            // Legacy: Parse ISO string to extract date components (avoid timezone conversion)
            const expiresAtStr = route.expiresAt // expiresAt is string | null, so if truthy it's a string
            const dateMatch = expiresAtStr.match(/^(\d{4})-(\d{2})-(\d{2})/)
            if (dateMatch) {
              const year = parseInt(dateMatch[1], 10)
              const month = parseInt(dateMatch[2], 10) - 1
              const day = parseInt(dateMatch[3], 10)
              routeDate = new Date(year, month, day, 0, 0, 0, 0)
            } else {
              const expiresAtDate = new Date(route.expiresAt)
              const year = expiresAtDate.getFullYear()
              const month = expiresAtDate.getMonth()
              const day = expiresAtDate.getDate()
              routeDate = new Date(year, month, day, 0, 0, 0, 0)
            }
          }
          
          if (routeDate) {
            routeDate.setHours(23, 59, 59) // End of route day
            const selectedDateObj = new Date(selectedDate)
            selectedDateObj.setHours(0, 0, 0, 0)
            
            if (selectedDateObj > routeDate) {
              console.log(`[Time Slot] Route date has passed for selected date`)
              continue // Skip routes that have passed
            }
          }
        }

        const timeWindow = formatTimeString(departureTime)
        console.log(`[Time Slot] Adding slot for route:`, timeWindow)

        slots.push({
          routeId: route.routeId,
          driverId: route.driverId,
          driverName: route.driverName,
          driverVehicle: route.driverVehicle,
          driverPhone: route.driverPhone,
          timeWindow,
          extraInfo: route.extraInfo || null
        })
      }

      // Sort by time
      slots.sort((a, b) => {
        const timeA = a.timeWindow.split(' - ')[0]
        const timeB = b.timeWindow.split(' - ')[0]
        return timeA.localeCompare(timeB)
      })

      console.log('[Time Slot] Generated slots:', slots.length, 'for date:', selectedDate, 'day:', dayOfWeek, 'dropoff:', selectedDropoff)
      setTimeSlots(slots)
    }

    calculateSlots()
  }, [selectedHub, selectedDropoff, selectedDate, hubs])

  // Handle hub selection
  const handleHubSelect = (hubName: string) => {
    setSelectedHub(hubName)
    setStep(2) // Go to dropoff location selection
  }

  // Handle dropoff location selection
  const handleDropoffSelect = (dropoffLocation: string) => {
    setSelectedDropoff(dropoffLocation)
    setStep(3) // Go to date selection
  }

  // Handle date selection
  const handleDateSelect = (date: Date) => {
    // Format date as YYYY-MM-DD using local timezone (not UTC)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const dateString = `${year}-${month}-${day}`
    console.log('[Date Selection] Selected date:', {
      originalDate: date.toISOString(),
      localDateString: dateString,
      dayOfWeek: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][date.getDay()]
    })
    setSelectedDate(dateString)
    setStep(4) // Go to time slot selection
  }

  // Handle time slot selection
  const handleTimeSlotSelect = (slot: typeof timeSlots[0]) => {
    if (!selectedHub || !selectedDropoff || !selectedDate) return
    
    setSelectedRun({
      hub: selectedHub,
      date: selectedDate,
      timeSlot: slot
    })
    setStep(5) // Go to confirmation
  }

  // Handle back navigation
  const handleBack = () => {
    if (step > 1) {
      // Clear step-specific data when going back
      if (step === 6) {
        // From payment, go back to confirmation
        setStep(5)
      } else if (step === 5) {
        // From confirmation, go back to time slot selection
        setStep(4)
      } else if (step === 4) {
        // From time slot selection, go back to date selection
        setSelectedDate(null)
        setTimeSlots([])
        setStep(3)
      } else if (step === 3) {
        // From date selection, go back to dropoff selection
        setSelectedDate(null)
        setAvailableDates([])
        setStep(2)
      } else if (step === 2) {
        // From dropoff selection, go back to hub selection
        setSelectedDropoff(null)
        setStep(1)
      }
    }
  }

  // Validate promo code
  const handlePromoCodeValidation = async () => {
    if (!promoCode.trim()) {
      setPromoCodeData(null)
      setPromoCodeError('')
      return
    }

    setValidatingPromoCode(true)
    setPromoCodeError('')

    try {
      // Include parentId in validation to check if this user has already used a one-time promo code
      const parentIdParam = user?.id ? `&parentId=${encodeURIComponent(user.id)}` : ''
      const response = await authenticatedGet(`/api/promo-codes/validate?code=${encodeURIComponent(promoCode.trim())}${parentIdParam}`)
      const data = await response.json()

      if (!response.ok || !data.valid) {
        setPromoCodeData(null)
        setPromoCodeError(data.error || 'Invalid promo code')
        return
      }

      setPromoCodeData(data.promoCode)
      setPromoCodeError('')
    } catch (err: any) {
      setPromoCodeData(null)
      setPromoCodeError('Failed to validate promo code. Please try again.')
    } finally {
      setValidatingPromoCode(false)
    }
  }

  // Handle order confirmation
  const fetchCredits = async () => {
    if (!user?.id) return
    
    try {
      const userResponse = await authenticatedGet(`/api/users?id=${user.id}`)
      const userData = await userResponse.json()
      if (userResponse.ok && userData.user) {
        setCredits(userData.user.credits || 0)
      }
    } catch (error) {
      console.error('Error fetching user credits:', error)
    }
  }

  const handleConfirm = async () => {
    if (!selectedRun || !selectedStudent || !user || !bagImageFile) return

    try {
      setSubmitting(true)
      
      // Find the route to verify it exists
      const hub = hubs.find(h => h.hub === selectedRun.hub)
      const route = hub?.routes.find(r => r.routeId === selectedRun.timeSlot.routeId)
      
      if (!route) {
        throw new Error('Route not found')
      }

      // Use selectedDropoff instead of route.destination
      if (!selectedDropoff) {
        throw new Error('Dropoff location not selected')
      }

      // Compress and convert bag image to base64
      const compressImage = (file: File, maxWidth: number = 1920, quality: number = 0.8): Promise<string> => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = (e) => {
            const img = new Image()
            img.onload = () => {
              const canvas = document.createElement('canvas')
              let width = img.width
              let height = img.height

              // Resize if too large
              if (width > maxWidth) {
                height = (height * maxWidth) / width
                width = maxWidth
              }

              canvas.width = width
              canvas.height = height

              const ctx = canvas.getContext('2d')
              if (!ctx) {
                reject(new Error('Could not get canvas context'))
                return
              }

              ctx.drawImage(img, 0, 0, width, height)
              
              // Convert to base64 with compression
              const base64String = canvas.toDataURL('image/jpeg', quality)
              resolve(base64String)
            }
            img.onerror = reject
            img.src = e.target?.result as string
          }
          reader.onerror = reject
          reader.readAsDataURL(file)
        })
      }

      const bagImageBase64 = await compressImage(bagImageFile)

      const response = await authenticatedPost('/api/orders', {
        parentId: user.id,
        pickup: selectedRun.hub,
        dropoff: selectedDropoff,
        studentId: selectedStudent.id,
        driverId: selectedRun.timeSlot.driverId,
        routeId: selectedRun.timeSlot.routeId,
        scheduledDate: selectedRun.date,
        scheduledTime: selectedRun.timeSlot.timeWindow,
        promoCode: promoCodeData?.code || null, // Send promo code if validated
        bagImage: bagImageBase64, // Include bag image
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to create order')
      }

      const responseData = await response.json()
      // API returns: { success: true, orderId: string, order: Order, message: string }
      const orderData = responseData.order || responseData // Fallback for compatibility
      const orderIdValue = responseData.orderId || orderData.id
      setOrderId(orderIdValue)
      
      // Use order.totalFee as the source of truth (already calculated correctly with promo discount)
      const orderTotalFee = orderData.totalFee || 10.0
      setOrderAmount(orderTotalFee)
      
      // Fetch driver details for confirmation screen
      const driverResponse = await fetch(`/api/users?id=${selectedRun.timeSlot.driverId}`)
      const driverData = await driverResponse.json()
      
      setConfirmationData({
        orderId: orderIdValue,
        hub: selectedRun.hub,
        date: selectedRun.date,
        timeWindow: selectedRun.timeSlot.timeWindow,
        driver: {
          name: selectedRun.timeSlot.driverName,
          vehicle: selectedRun.timeSlot.driverVehicle,
          phone: selectedRun.timeSlot.driverPhone,
        },
        student: selectedStudent,
        totalFee: orderTotalFee, // Use the same source of truth
      })
      
      // Go to payment step instead of final confirmation
      setStep(6)
    } catch (error: any) {
      console.error('Error creating order:', error)
      alert(error.message || 'Failed to create order. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  // "Today" pinned to Central Time, as a local-midnight Date so it compares
  // cleanly against the local-midnight Dates the calendar grid builds. This is
  // what makes a rider outside Texas see the same available dates as one in Austin.
  const centralToday = parseCalendarDate(getCentralTodayString()) ?? new Date()

  // Check if a grid date is today (Central)
  const isToday = (date: Date) => {
    return date.toDateString() === centralToday.toDateString()
  }

  // Check if a grid date is in the past (before Central today)
  const isPast = (date: Date) => {
    return date < centralToday
  }

  const steps = [
    { number: 1, label: 'Pick Hub', title: 'Pick Location (Hub)' },
    { number: 2, label: 'Pick Dropoff', title: 'Pick Dropoff Location' },
    { number: 3, label: 'Pick Date', title: 'Pick Date' },
    { number: 4, label: 'Pick Time', title: 'Pick Time Slot' },
    { number: 5, label: 'Confirm', title: 'Confirm Booking' },
    { number: 6, label: 'Payment', title: 'Payment' },
  ]

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
          <div className="text-center">Loading...</div>
        </div>
      </div>
    )
  }

  if (!user || user.type !== 'parent') {
    return (
      <div className="min-h-screen bg-cream">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
            <p className="text-gray-600">Please log in as a parent to create an order.</p>
          </div>
        </div>
      </div>
    )
  }

  // Step 6: Payment screen
  if (step === 6 && orderId && confirmationData) {
    return (
      <div className="min-h-screen bg-cream">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-8">
            {/* Left Side: Steps Progress */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-cream-deep sticky top-24">
                <div className="space-y-6">
                  {steps.map((s, index) => (
                    <div key={s.number} className="flex items-start">
                      <div className="flex flex-col items-center">
                        {/* Step Circle */}
                        <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                          s.number < 6
                            ? 'bg-ember border-ember text-white'
                            : s.number === 6
                            ? 'bg-ember border-ember text-white'
                            : 'border-gray-300 text-gray-400'
                        }`}>
                          {s.number < 6 ? (
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <span className="text-sm font-semibold">{s.number}</span>
                          )}
                        </div>
                        {/* Vertical Line */}
                        {index < steps.length - 1 && (
                          <div className={`w-0.5 h-16 mt-2 ${
                            s.number < 6 ? 'bg-ember' : 'bg-gray-300'
                          }`} />
                        )}
                      </div>
                      {/* Step Label */}
                      <div className="ml-4 pt-2">
                        <div className={`text-sm font-medium ${
                          s.number <= 6 ? 'text-gray-900' : 'text-gray-400'
                        }`}>
                          {s.label}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Side: Payment Content */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl p-4 sm:p-8 shadow-sm border border-cream-deep">
                <button
                  onClick={handleBack}
                  className="mb-4 flex items-center text-gray-600 hover:text-gray-900 transition"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </button>
                <h1 className="text-2xl font-serif font-semibold tracking-tight text-ink mb-2">{steps[4].title}</h1>
                <p className="text-gray-600 mb-6">Complete your payment to confirm your order</p>

                {/* Order Summary */}
                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h2>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Hub:</span>
                      <span className="font-medium">{confirmationData.hub}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Date:</span>
                      <span className="font-medium">{formatCalendarDate(confirmationData.date, 'short')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Time Window:</span>
                      <span className="font-medium">{confirmationData.timeWindow}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Student:</span>
                      <span className="font-medium">{confirmationData.student.name}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-gray-200">
                      <span className="text-lg font-semibold text-gray-900">Total:</span>
                      <span className="text-lg font-semibold text-ember">${orderAmount.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Available Credits Display */}
                {credits > 0 && (
                  <div className="bg-ember-soft/60 border border-ember/20 rounded-2xl p-6 shadow-sm mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <svg className="w-6 h-6 text-ember" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                          <p className="text-sm text-gray-600">Available Credits</p>
                          <p className="text-2xl font-bold text-ember">${credits.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Pay with Credits Button - Show if user has enough credits */}
                    {credits >= orderAmount && (
                      <div className="mb-4">
                        <button
                          onClick={async () => {
                            if (!user?.id || !orderId) return
                            
                            if (!termsAccepted) {
                              alert('Please accept the Terms of Service before proceeding with payment.')
                              return
                            }
                            
                            if (credits < orderAmount) {
                              alert(`Insufficient credits. You have $${credits.toFixed(2)} but need $${orderAmount.toFixed(2)}.`)
                              return
                            }

                            if (!confirm(`Pay $${orderAmount.toFixed(2)} using your credits?`)) {
                              return
                            }

                            setPayingWithCredits(true)
                            try {
                              const response = await fetch('/api/stripe/payment-intent', {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                },
                                body: JSON.stringify({
                                  orderId,
                                  parentId: user.id,
                                  amount: orderAmount,
                                  useCredits: true,
                                }),
                              })

                              const data = await response.json()

                              if (!response.ok) {
                                throw new Error(data.error || 'Failed to process payment with credits')
                              }

                              if (data.fullyPaidWithCredits) {
                                // Payment successful with credits
                                setStep(6)
                              } else {
                                throw new Error('Unexpected response from payment processor')
                              }
                            } catch (error: any) {
                              console.error('Pay with credits error:', error)
                              setPaymentError(error.message || 'Failed to pay with credits. Please try again.')
                            } finally {
                              setPayingWithCredits(false)
                            }
                          }}
                          disabled={payingWithCredits || !termsAccepted}
                          className="w-full bg-ember text-white py-4 rounded-full font-semibold text-lg hover:bg-ember-hover shadow-md shadow-ember/25 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-h-[44px]"
                        >
                          {payingWithCredits ? (
                            <>
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                              Processing...
                            </>
                          ) : (
                            <>
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Pay ${orderAmount.toFixed(2)} with Credits
                            </>
                          )}
                        </button>
                        <p className="text-xs text-gray-600 text-center mt-2">
                          Your credits will cover the full order amount
                        </p>
                      </div>
                    )}

                    {/* Partial Credits Option */}
                    {credits > 0 && credits < orderAmount && (
                      <div className="mb-4">
                        <label className="flex items-center gap-2 cursor-pointer mb-3">
                          <input
                            type="checkbox"
                            checked={useCredits}
                            onChange={(e) => setUseCredits(e.target.checked)}
                            className="w-5 h-5 text-ember border-gray-300 rounded focus:ring-ember"
                          />
                          <span className="text-sm font-medium text-gray-700">Use ${credits.toFixed(2)} in credits (${(orderAmount - credits).toFixed(2)} remaining)</span>
                        </label>
                      </div>
                    )}
                    
                    {useCredits && credits < orderAmount && (
                      <div className="mt-4 pt-4 border-t border-ember/20">
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Order Total:</span>
                            <span className="text-gray-900 font-medium">${orderAmount.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Credits Applied:</span>
                            <span className="text-green-600 font-medium">
                              -${Math.min(credits, orderAmount).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between pt-2 border-t border-ember/20">
                            <span className="text-gray-900 font-semibold">Amount to Pay:</span>
                            <span className="text-xl font-bold text-gray-900">
                              ${Math.max(0, orderAmount - Math.min(credits, orderAmount)).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Terms of Service Acceptance */}
                <div className="bg-ember-soft/60 border border-ember/20 rounded-lg p-6 mb-6">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      className="mt-1 w-5 h-5 text-ember border-gray-300 rounded focus:ring-ember"
                      required
                    />
                    <div className="flex-1">
                      <p className="text-sm text-gray-700">
                        By clicking this button, you agree to our{' '}
                        <Link 
                          href="/terms" 
                          target="_blank"
                          className="text-ember underline hover:text-ember-hover font-medium"
                        >
                          Terms of Service
                        </Link>
                        {' '}and{' '}
                        <Link 
                          href="/refund-policy" 
                          target="_blank"
                          className="text-ember underline hover:text-ember-hover font-medium"
                        >
                          Refunds & Cancellations Policy
                        </Link>
                      </p>
                    </div>
                  </label>
                </div>

                {/* Payment Form */}
                {paymentError && (
                  <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg mb-6">
                    {paymentError}
                  </div>
                )}

                {!termsAccepted && (
                  <div className="bg-yellow-50 border border-yellow-300 text-yellow-700 px-4 py-3 rounded-lg mb-6">
                    Please accept the Terms of Service to proceed with payment.
                  </div>
                )}

                {/* Divider if both options are available */}
                {credits > 0 && (
                  <div className="my-6 flex items-center">
                    <div className="flex-1 border-t border-gray-300"></div>
                    <span className="px-4 text-sm text-gray-500">OR</span>
                    <div className="flex-1 border-t border-gray-300"></div>
                  </div>
                )}

                {/* Stripe Payment Form - Always available */}
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Pay with Card</h3>
                  <div className={!termsAccepted ? 'opacity-50 pointer-events-none' : ''}>
                    <PaymentButton
                      orderId={orderId}
                      amount={useCredits ? Math.max(0, orderAmount - Math.min(credits, orderAmount)) : orderAmount}
                      useCredits={useCredits}
                      onSuccess={() => {
                        // Payment successful - redirect to order details page
                        if (orderId) {
                          router.push(`/rider/orders/${orderId}`)
                        } else {
                          // Fallback to orders list if orderId is not available
                          router.push('/rider/orders?payment=success')
                        }
                      }}
                      onError={(error) => {
                        setPaymentError(error)
                      }}
                    />
                  </div>
                  {!termsAccepted && (
                    <p className="text-sm text-gray-500 mt-2 text-center">
                      Please accept the Terms of Service above to enable payment
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Step 6: Final Confirmation screen
  if (step === 6 && confirmationData) {
    return (
      <div className="min-h-screen bg-cream">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-8">
            {/* Left Side: All Steps Completed */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-cream-deep sticky top-24">
                <div className="space-y-6">
                  {steps.map((s, index) => (
                    <div key={s.number} className="flex items-start">
                      <div className="flex flex-col items-center">
                        {/* Step Circle - All completed */}
                        <div className="flex items-center justify-center w-10 h-10 rounded-full border-2 bg-ember border-ember text-white">
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        {/* Vertical Line */}
                        {index < steps.length - 1 && (
                          <div className="w-0.5 h-16 mt-2 bg-ember" />
                        )}
                      </div>
                      {/* Step Label */}
                      <div className="ml-4 pt-2">
                        <div className="text-sm font-medium text-gray-900">
                          {s.label}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Side: Confirmation Content */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl p-4 sm:p-8 shadow-sm border border-cream-deep">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-ember-soft rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-ember" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h1 className="text-3xl font-serif font-semibold tracking-tight text-ink mb-2">Order Confirmed!</h1>
                  <p className="text-gray-600">Your order has been confirmed and payment received. Your driver will be notified.</p>
                </div>

            <div className="space-y-6 mb-8">
              <div className="border-b pb-4">
                <h2 className="text-sm font-medium text-gray-500 mb-2">Delivery Details</h2>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Hub:</span>
                    <span className="font-medium">{confirmationData.hub}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Date:</span>
                    <span className="font-medium">{formatCalendarDate(confirmationData.date, 'short')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Time Window:</span>
                    <span className="font-medium">{confirmationData.timeWindow}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Student:</span>
                    <span className="font-medium">{confirmationData.student.name}</span>
                  </div>
                </div>
              </div>

              <div className="border-b pb-4">
                <h2 className="text-sm font-medium text-gray-500 mb-2">Driver Information</h2>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Driver:</span>
                    <span className="font-medium">{confirmationData.driver.name}</span>
                  </div>
                  {confirmationData.driver.vehicle && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Vehicle:</span>
                      <span className="font-medium">{confirmationData.driver.vehicle}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Phone:</span>
                    <span className="font-medium">{confirmationData.driver.phone}</span>
                  </div>
                </div>
              </div>
            </div>

                <div className="flex gap-4 mt-8">
                  <button
                    onClick={() => router.push('/rider/orders')}
                    className="flex-1 border border-ink/15 text-ink bg-white py-3 px-6 rounded-full font-semibold hover:border-ink/30 transition min-h-[44px]"
                  >
                    View Orders
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Left Side: Vertical Progress Indicator */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-cream-deep sticky top-24">
              <div className="space-y-6">
                {steps.map((s, index) => (
                  <div key={s.number} className="flex items-start">
                    <div className="flex flex-col items-center">
                      {/* Step Circle */}
                      <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all ${
                        step > s.number
                          ? 'bg-ember border-ember text-white'
                          : step === s.number
                          ? 'bg-ember border-ember text-white'
                          : 'bg-white border-gray-300 text-gray-400'
                      }`}>
                        {step > s.number ? (
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <span className="font-semibold text-sm">{s.number}</span>
                        )}
                      </div>
                      {/* Vertical Line */}
                      {index < steps.length - 1 && (
                        <div className={`w-0.5 h-16 mt-2 ${
                          step > s.number ? 'bg-ember' : 'bg-gray-300'
                        }`} />
                      )}
                    </div>
                    {/* Step Label */}
                    <div className="ml-4 pt-2">
                      <div className={`text-sm font-medium ${
                        step >= s.number ? 'text-gray-900' : 'text-gray-400'
                      }`}>
                        {s.label}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Side: Main Content */}
          <div className="lg:col-span-3">

            {/* Step 1: Hub Selection */}
            {step === 1 && (
              <div className="bg-white rounded-2xl p-4 sm:p-8 shadow-sm border border-cream-deep">
                <h1 className="text-2xl font-serif font-semibold tracking-tight text-ink mb-2">{steps[0].title}</h1>
                <p className="text-gray-600 mb-8">Select your closest pickup hub</p>
            
            {loadingHubs ? (
              <div className="text-center py-12">Loading hubs...</div>
            ) : filteredHubs.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600">
                  {selectionMode === 'address' && homeAddressCoords
                    ? 'No routes found within your selected radius. Try increasing the search radius.'
                    : 'No hubs available at this time.'}
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {filteredHubs.map((hub) => (
                  <button
                    key={hub.hub}
                    onClick={() => handleHubSelect(hub.hub)}
                    className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition text-left border border-gray-200 hover:border-ember/30 hover:bg-ember-soft"
                  >
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{hub.hub}</h3>
                    <p className="text-sm text-gray-600">
                      {hub.routes.length} route{hub.routes.length !== 1 ? 's' : ''} available
                      {hub.distance !== undefined && (
                        <span className="ml-2 text-ember">
                          ({hub.distance.toFixed(1)} miles away)
                        </span>
                      )}
                    </p>
                    {hub.routes.some(route => route.extraInfo) && (
                      <div className="mt-2 text-xs text-gray-500 italic">
                        ℹ️ Some routes have additional pickup instructions
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

            {/* Step 2: Dropoff Location Selection */}
            {step === 2 && selectedHub && (
              <div className="bg-white rounded-2xl p-4 sm:p-8 shadow-sm border border-cream-deep">
                <button
                  onClick={handleBack}
                  className="mb-4 flex items-center text-gray-600 hover:text-gray-900 transition"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </button>
                <h1 className="text-2xl font-serif font-semibold tracking-tight text-ink mb-2">{steps[1].title}</h1>
                <p className="text-gray-600 mb-6">Select the dropoff location for your route from {selectedHub}</p>

                {(() => {
                  // Get unique dropoff locations from routes for this hub
                  const hub = hubs.find(h => h.hub === selectedHub)
                  if (!hub) return null
                  
                  const dropoffLocations = Array.from(new Set(hub.routes.map(r => r.destination)))
                  
                  return (
                    <div className="space-y-4">
                      {dropoffLocations.map((dropoff) => (
                        <button
                          key={dropoff}
                          onClick={() => handleDropoffSelect(dropoff)}
                          className={`w-full bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition text-left border ${
                            selectedDropoff === dropoff
                              ? 'border-ember bg-ember-soft'
                              : 'border-gray-200 hover:border-ember/30 hover:bg-ember-soft'
                          }`}
                        >
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">{dropoff}</h3>
                          <p className="text-sm text-gray-600">
                            {hub.routes.filter(r => r.destination === dropoff).length} route{hub.routes.filter(r => r.destination === dropoff).length !== 1 ? 's' : ''} available
                          </p>
                        </button>
                      ))}
                    </div>
                  )
                })()}
              </div>
            )}

            {/* Step 3: Date Selection */}
            {step === 3 && selectedHub && selectedDropoff && (
              <div className="bg-white rounded-2xl p-4 sm:p-8 shadow-sm border border-cream-deep">
                <button
                  onClick={handleBack}
                  className="mb-4 flex items-center text-gray-600 hover:text-gray-900 transition"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </button>
                <h1 className="text-2xl font-serif font-semibold tracking-tight text-ink mb-2">{steps[2].title}</h1>
                <p className="text-gray-600 mb-6">Select a date for pickup at {selectedHub}</p>

            {availableDates.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600">No available dates for this hub.</p>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-6">
                {/* Calendar header with day names */}
                <div className="grid grid-cols-7 gap-2 mb-2">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                    <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                      {day}
                    </div>
                  ))}
                </div>
                
                {/* Calendar grid - show full month view */}
                <div className="grid grid-cols-7 gap-2">
                  {(() => {
                    // Month view anchored on Central today so the visible month is
                    // the same for a rider in Tokyo and one in Austin
                    const today = parseCalendarDate(getCentralTodayString()) ?? new Date()

                    // Get first day of current month
                    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
                    const startDayOfWeek = firstDayOfMonth.getDay()
                    
                    // Create a Set of available date strings for quick lookup (using local timezone)
                    const availableDateSet = new Set(
                      availableDates.map(d => {
                        const date = new Date(d)
                        date.setHours(0, 0, 0, 0)
                        // Format as YYYY-MM-DD using local timezone
                        const year = date.getFullYear()
                        const month = String(date.getMonth() + 1).padStart(2, '0')
                        const day = String(date.getDate()).padStart(2, '0')
                        return `${year}-${month}-${day}`
                      })
                    )
                    
                    const days: (Date | null)[] = []
                    
                    // Add empty cells for days before first day of month
                    for (let i = 0; i < startDayOfWeek; i++) {
                      days.push(null)
                    }
                    
                    // Add all days of current and next month (to show ~6 weeks)
                    const currentDate = new Date(firstDayOfMonth)
                    for (let i = 0; i < 42; i++) { // 6 weeks = 42 days
                      days.push(new Date(currentDate))
                      currentDate.setDate(currentDate.getDate() + 1)
                    }
                    
                    return days.map((date, index) => {
                      if (!date) {
                        return <div key={`empty-${index}`} className="p-4" />
                      }
                      
                      // Format date as YYYY-MM-DD using local timezone (not UTC)
                      const year = date.getFullYear()
                      const month = String(date.getMonth() + 1).padStart(2, '0')
                      const day = String(date.getDate()).padStart(2, '0')
                      const dateStr = `${year}-${month}-${day}`
                      const isAvailable = availableDateSet.has(dateStr)
                      const isSelected = selectedDate === dateStr
                      const isPastDate = isPast(date)
                      const isTodayDate = isToday(date)
                      
                      return (
                        <button
                          key={dateStr}
                          onClick={() => isAvailable && !isPastDate && handleDateSelect(date)}
                          disabled={!isAvailable || isPastDate}
                          className={`relative p-3 rounded-lg border transition-all min-h-[60px] flex flex-col items-center justify-center ${
                            isSelected && isAvailable
                              ? 'bg-ember border-ember text-white shadow-md'
                              : !isAvailable
                              ? 'bg-red-50 border-red-200 text-red-400 cursor-not-allowed opacity-60'
                              : isPastDate
                              ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
                              : isTodayDate && isAvailable
                              ? 'bg-ember-soft border-ember/30 hover:border-ember hover:bg-ember-soft'
                              : isAvailable
                              ? 'bg-white border-ember/30 hover:border-ember hover:bg-ember-soft'
                              : 'bg-white border-gray-200'
                          }`}
                        >
                          {/* Day number */}
                          <div className={`text-sm font-semibold mb-1 ${
                            isSelected && isAvailable 
                              ? 'text-white' 
                              : !isAvailable 
                              ? 'text-red-400' 
                              : isPastDate 
                              ? 'text-gray-400' 
                              : 'text-gray-900'
                          }`}>
                            {date.getDate()}
                          </div>
                          
                          {/* Event indicator dot - only show for available dates */}
                          {isAvailable && !isPastDate && (
                            <div className={`w-1.5 h-1.5 rounded-full ${
                              isSelected ? 'bg-white' : 'bg-ember'
                            }`} />
                          )}
                          
                          {/* Today indicator */}
                          {isTodayDate && !isSelected && isAvailable && (
                            <div className="absolute top-1 right-1 w-2 h-2 bg-ember rounded-full" />
                          )}
                        </button>
                      )
                    })
                  })()}
                </div>
              </div>
            )}
          </div>
        )}

            {/* Step 4: Time Slot Selection */}
            {step === 4 && selectedHub && selectedDropoff && selectedDate && (
              <div className="bg-white rounded-2xl p-4 sm:p-8 shadow-sm border border-cream-deep">
                <button
                  onClick={handleBack}
                  className="mb-4 flex items-center text-gray-600 hover:text-gray-900 transition"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </button>
                <h1 className="text-2xl font-serif font-semibold tracking-tight text-ink mb-2">{steps[3].title}</h1>
                <p className="text-gray-600 mb-6">
                  Select a time window for {formatCalendarDate(selectedDate, 'short')}
                </p>

            {timeSlots.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600">No time slots available for this date.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {timeSlots.map((slot, index) => (
                  <button
                    key={`${slot.routeId}-${index}`}
                    onClick={() => handleTimeSlotSelect(slot)}
                    className="w-full bg-white rounded-lg p-5 shadow-sm hover:shadow-md transition text-left border border-gray-200 hover:border-ember/30 hover:bg-ember-soft"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-lg font-semibold text-gray-900 mb-1">
                          Driver leaving at {slot.timeWindow}
                        </div>
                        <div className="text-sm text-gray-600">
                          Driver: {slot.driverName}
                        </div>
                        {slot.extraInfo && (
                          <div className="mt-2 text-xs text-gray-500 italic">
                            ℹ️ {slot.extraInfo}
                          </div>
                        )}
                      </div>
                      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

            {/* Step 5: Confirmation */}
            {step === 5 && selectedRun && (
              <div className="bg-white rounded-2xl p-4 sm:p-8 shadow-sm border border-cream-deep">
                <button
                  onClick={handleBack}
                  className="mb-4 flex items-center text-gray-600 hover:text-gray-900 transition"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </button>
                <h1 className="text-2xl font-serif font-semibold tracking-tight text-ink mb-2">{steps[4].title}</h1>
                <p className="text-gray-600 mb-6">Review your order details</p>

                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <div className="space-y-4">
                    <div className="flex justify-between pb-4 border-b border-gray-200">
                      <span className="text-gray-600">Hub:</span>
                      <span className="font-semibold text-gray-900">{selectedRun.hub}</span>
                    </div>
                    <div className="flex justify-between pb-4 border-b border-gray-200">
                      <span className="text-gray-600">Date:</span>
                      <span className="font-semibold text-gray-900">{formatCalendarDate(selectedRun.date, 'short')}</span>
                    </div>
                    <div className="flex justify-between pb-4 border-b border-gray-200">
                      <span className="text-gray-600">Time Window:</span>
                      <span className="font-semibold text-gray-900">{selectedRun.timeSlot.timeWindow}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Student:</span>
                      <span className="font-semibold text-gray-900">
                        {selectedStudent?.name || 'Not selected'}
                      </span>
                    </div>
                    {promoCodeData && (
                      <div className="flex justify-between pt-4 border-t border-gray-200">
                        <span className="text-gray-600">Promo Code ({promoCodeData.code}):</span>
                        <span className="font-semibold text-green-600">
                          Applied
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Promo Code Section */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Promo Code (Optional)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      onBlur={handlePromoCodeValidation}
                      placeholder="Enter promo code"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ember focus:border-ember"
                    />
                    <button
                      type="button"
                      onClick={handlePromoCodeValidation}
                      disabled={validatingPromoCode || !promoCode}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {validatingPromoCode ? 'Validating...' : 'Apply'}
                    </button>
                  </div>
                  {promoCodeError && (
                    <p className="mt-2 text-sm text-red-600">{promoCodeError}</p>
                  )}
                  {promoCodeData && (
                    <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-800">
                        <strong>✓ {promoCodeData.code}</strong> applied! 
                        {promoCodeData.description && ` ${promoCodeData.description}`}
                      </p>
                    </div>
                  )}
                </div>

                {students.length > 0 && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Student {selectedStudent && <span className="text-green-600 text-sm">(Selected: {selectedStudent.name})</span>}
                    </label>
                    <select
                      value={selectedStudent?.id || ""}
                      onChange={(e) => {
                        const studentId = e.target.value
                        if (!studentId) {
                          setSelectedStudent(null)
                          return
                        }
                        const student = students.find((s) => s.id === studentId)
                        if (student) {
                          setSelectedStudent(student)
                        }
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ember focus:border-ember"
                    >
                      <option value="">Choose a student...</option>
                      {students.map((student) => (
                        <option key={student.id} value={student.id}>
                          {student.name} {student.email ? `(${student.email})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Bag Image Upload */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Order Bag Photo <span className="text-red-500">*</span>
                  </label>
                  <div className="space-y-3">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file) {
                          // Validate file type
                          if (!file.type.startsWith('image/')) {
                            setBagImageError('Please upload an image file')
                            setBagImageFile(null)
                            setBagImagePreview(null)
                            return
                          }
                          // Validate file size (max 10MB)
                          if (file.size > 10 * 1024 * 1024) {
                            setBagImageError('File size must be less than 10MB')
                            setBagImageFile(null)
                            setBagImagePreview(null)
                            return
                          }
                          
                          setBagImageFile(file)
                          setBagImageError(null)
                          
                          // Create preview
                          const reader = new FileReader()
                          reader.onloadend = () => {
                            setBagImagePreview(reader.result as string)
                          }
                          reader.readAsDataURL(file)
                        } else {
                          setBagImageFile(null)
                          setBagImagePreview(null)
                        }
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ember focus:border-ember file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-ember-soft file:text-ember hover:file:bg-ember-soft"
                    />
                    {bagImageError && (
                      <p className="text-sm text-red-600">{bagImageError}</p>
                    )}
                    {bagImagePreview && (
                      <div className="mt-3">
                        <p className="text-sm text-gray-600 mb-2">Preview:</p>
                        <img 
                          src={bagImagePreview} 
                          alt="Bag preview" 
                          className="max-w-full h-auto max-h-64 rounded-lg border border-gray-300 shadow-sm"
                        />
                      </div>
                    )}
                    {!bagImageFile && (
                      <p className="text-xs text-gray-500">
                        Please upload a photo of your order bag. This helps drivers identify your order.
                      </p>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (!submitting && selectedStudent && !validatingPromoCode && bagImageFile) {
                      handleConfirm()
                    }
                  }}
                  disabled={submitting || !selectedStudent || validatingPromoCode || !bagImageFile}
                  className="w-full bg-ember text-white py-3 px-6 rounded-full font-semibold hover:bg-ember-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 relative z-10 min-h-[44px]"
                  style={{ pointerEvents: (submitting || !selectedStudent || validatingPromoCode || !bagImageFile) ? 'none' : 'auto' }}
                >
                  {submitting ? (
                    <>
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Confirming...
                    </>
                  ) : (
                    'Confirm Delivery'
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default function NewOrderPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-cream">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
          <div className="text-center">Loading...</div>
        </div>
      </div>
    }>
      <NewOrderPageContent />
    </Suspense>
  )
}
